import React, { useState, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Chip,
    Collapse,
    IconButton,
    CircularProgress
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ProcessTreeItem from './ProcessTreeItem';

const statusConfigs = {
    idle: { icon: null, color: 'default', label: 'Idle' },
    processing: { icon: <CircularProgress size={16} thickness={5} />, color: 'primary', label: 'Processing...' },
    success: { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Process Complete' },
    error: { icon: <ErrorIcon fontSize="small" />, color: 'error', label: 'Error' }
};

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
}));

const ProcessUpdates = ({
    processTree = [],
    status = 'processing',
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = useCallback(() => {
        setIsExpanded(prev => !prev);
    }, []);

    const overallProcessStatus = useMemo(() => {
        if (status === 'error') {
            return 'error';
        }
        if (!processTree || processTree.length === 0) {
            return status || 'processing';
        }
        const checkForError = (nodes) => {
            for (const node of nodes) {
                if (node.status === 'error') return true;
                if (node.children && checkForError(node.children)) return true;
            }
            return false;
        }
        if (checkForError(processTree)) return 'error';
        const isAnyProcessRunning = processTree.some(p => p.status === 'processing');
        return isAnyProcessRunning ? 'processing' : 'success';
    }, [processTree, status]);

    const config = statusConfigs[overallProcessStatus] || statusConfigs.processing;

    const chipSx = overallProcessStatus === 'success'
        ? {
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
            color: (theme) => theme.palette.success.dark,
            '& .MuiChip-icon': {
                color: (theme) => theme.palette.success.dark,
            },
        }
        : {};

    if (processTree.length === 0) return null;

    return (
        <Box sx={{ width: 'fit-content' }}>
            <HeaderBox onClick={toggleExpand} aria-expanded={isExpanded}>
                <Chip
                    icon={config.icon}
                    label={config.label}
                    color={overallProcessStatus === 'success' ? 'default' : (config.color || 'default')}
                    size="small"
                    sx={chipSx}
                />
                <IconButton size="small" aria-label={isExpanded ? "Collapse" : "Expand"}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </HeaderBox>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pt: 1.5, mt: 1, borderTop: 1, borderColor: 'inherit' }}>
                    {processTree.map((process) => (
                        <ProcessTreeItem key={process.id} process={process} />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

ProcessUpdates.propTypes = {
    processTree: PropTypes.array,
    status: PropTypes.oneOf(['idle', 'processing', 'success', 'error']),
};

export default memo(ProcessUpdates);